import type { ReactNode } from 'react';
import type { MarketingError } from '../api/errors';
import { formatCount } from '../model/format';
import type { ResourcePhase } from '../model/resource';
import type { AccountCapacity, QuotaChannel } from '../model/types';
import { CapacityBanner } from './capacity-banner';
import { ChannelErrorState, ChannelLoading } from './states';
import { PageHeader } from '@/shared/components/page-header';
import { Card } from '@/shared/ui/card';

export function ChannelSettingsPage({
  title,
  description,
  phase = 'ready',
  error = null,
  onRetry,
  loadingLabel,
  errorTitle,
  capacity,
  channel,
  capacityTitle,
  unit,
  live = true,
  limitsNote,
  children,
}: {
  title: string;
  description: string;
  /** Fase da lista que a página mostra (remetentes, números). */
  phase?: ResourcePhase;
  error?: MarketingError | null;
  onRetry?: () => void;
  loadingLabel: string;
  errorTitle: string;
  capacity?: AccountCapacity;
  /** Obrigatório junto com `capacity`: é o canal que dá nome e unidade ao saldo. */
  channel?: QuotaChannel;
  capacityTitle?: string;
  unit?: string;
  live?: boolean;
  limitsNote?: string;
  children?: ReactNode;
}) {
  const header = <PageHeader title={title} description={description} />;

  if (phase === 'loading') {
    return (
      <>
        {header}
        <ChannelLoading label={loadingLabel} />
      </>
    );
  }

  if (phase === 'error') {
    return (
      <>
        {header}
        <ChannelErrorState title={errorTitle} error={error} onRetry={onRetry} />
      </>
    );
  }

  return (
    <div className="min-w-0 space-y-4">
      {header}
      {capacity && channel ? (
        <CapacityBanner capacity={capacity} channel={channel} title={capacityTitle} unit={unit} live={live} />
      ) : null}
      {capacity ? (
        <Card className="p-4 sm:p-5">
          <h2 className="text-sm font-medium">Limites internos</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {limitsNote ?? 'Aparecem de novo na revisão, antes da aprovação.'}
          </p>
          <dl className="mt-3 grid gap-3 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">Reserva para transacionais</dt>
              <dd className="text-lg font-semibold tabular-nums">{formatCount(capacity.transactionalReserve)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Limite da empresa</dt>
              <dd className="text-lg font-semibold tabular-nums">{formatCount(capacity.companyLimit)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Limite da campanha</dt>
              <dd className="text-lg font-semibold tabular-nums">{formatCount(capacity.campaignLimit)}</dd>
            </div>
          </dl>
        </Card>
      ) : null}
      {children}
    </div>
  );
}
